import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Send, Mail, Phone, MessageSquare, Clock } from 'lucide-react';

export interface ContactRequestFull {
  id: string;
  user_id: string | null;
  full_name: string;
  phone: string;
  email: string;
  subject: string;
  inquiry_type: string | null;
  message: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface ContactRequestDetailModalProps {
  request: ContactRequestFull | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusChange: (id: string, newStatus: string) => Promise<void>;
  onAfterResponse: () => void;
}

const ContactRequestDetailModal = ({
  request,
  isOpen,
  onClose,
  onStatusChange,
  onAfterResponse,
}: ContactRequestDetailModalProps) => {
  const [adminResponse, setAdminResponse] = useState('');
  const [sending, setSending] = useState(false);

  if (!request) return null;

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'in_progress':
        return 'default';
      case 'resolved':
        return 'outline';
      case 'closed':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'ממתין';
      case 'in_progress':
        return 'בטיפול';
      case 'resolved':
        return 'נפתר';
      case 'closed':
        return 'סגור';
      default:
        return 'ממתין';
    }
  };

  const handleSendResponse = async () => {
    if (!adminResponse.trim()) {
      toast.error('אנא הזן תגובה');
      return;
    }

    setSending(true);
    try {
      // Update status first so UI is instantly updated
      await onStatusChange(request.id, 'resolved');

      const { data, error } = await supabase.functions.invoke('send-contact-response', {
        body: {
          contactRequestId: request.id,
          responseMessage: adminResponse.trim(),
          adminName: 'צוות Excess Market',
        },
      });

      if (error) throw error;
      if (!data?.success) {
        throw new Error(data?.error || 'שגיאה בשליחת המייל');
      }

      toast.success('התגובה נשלחה בהצלחה לאימייל הלקוח');
      setAdminResponse('');
      onAfterResponse();
      onClose();
    } catch (error: any) {
      console.error('Error sending contact response:', error);
      toast.error(`שגיאה בשליחת התגובה: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-right">פרטי פנייה - {request.full_name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 text-right">
          {/* Top info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3 text-right">
              <div className="flex items-center gap-2 flex-row-reverse">
                <span className="font-medium">שם מלא:</span>
                <span>{request.full_name}</span>
              </div>
              <div className="flex items-center gap-2 flex-row-reverse">
                <Mail className="h-4 w-4" />
                <span className="font-medium">אימייל:</span>
                <span dir="ltr" className="text-left">{request.email}</span>
              </div>
              <div className="flex items-center gap-2 flex-row-reverse">
                <Phone className="h-4 w-4" />
                <span className="font-medium">טלפון:</span>
                <span dir="ltr" className="text-left">{request.phone}</span>
              </div>
            </div>

            <div className="space-y-3 text-right">
              <div className="flex items-center gap-2 flex-row-reverse">
                <MessageSquare className="h-4 w-4" />
                <span className="font-medium">נושא:</span>
                <span>{request.subject}</span>
              </div>
              <div className="flex items-center gap-2 flex-row-reverse">
                <span className="font-medium">סוג פנייה:</span>
                <span>{request.inquiry_type || 'אחר'}</span>
              </div>
              <div className="flex items-center gap-2 flex-row-reverse">
                <Clock className="h-4 w-4" />
                <span className="font-medium">נוצר:</span>
                <span>
                  {new Date(request.created_at).toLocaleString('he-IL')}
                </span>
              </div>
            </div>
          </div>

          {/* Customer message */}
          <div className="space-y-2">
            <Label className="text-right block">הודעת הלקוח</Label>
            <div className="bg-muted p-4 rounded-lg">
              <p className="whitespace-pre-wrap text-sm">{request.message}</p>
            </div>
          </div>

          {/* Status + response */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 justify-end flex-row-reverse">
              <span className="text-sm text-muted-foreground">סטטוס נוכחי:</span>
              <Badge variant={getStatusBadgeVariant(request.status)}>
                {getStatusLabel(request.status)}
              </Badge>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-response" className="text-right block">
                תגובת המנהל ללקוח
              </Label>
              <Textarea
                id="admin-response"
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
                placeholder="הכנס כאן את תגובתך ללקוח..."
                rows={5}
                className="resize-none text-right"
                dir="rtl"
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setAdminResponse('')}
                  disabled={sending}
                >
                  נקה
                </Button>
                <Button
                  onClick={handleSendResponse}
                  disabled={sending || !adminResponse.trim()}
                >
                  {sending ? 'שולח...' : (
                    <span className="flex items-center gap-1">
                      <Send className="h-4 w-4" />
                      <span>שלח תגובה</span>
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactRequestDetailModal;
